/**
 * Weapon synthesis.
 *
 * Every gunshot is five layers stacked with sample-accurate offsets:
 *
 *   1. TRANSIENT  — a sub-millisecond filtered impulse. This is the crack. It must
 *                   be the loudest thing in the buffer so the measured attack time
 *                   stays under ~1 ms.
 *   2. BODY       — band-limited noise through a resonant lowpass sweeping from a
 *                   few kHz down to a couple of hundred Hz in ~50 ms, driven into
 *                   asymmetric saturation. This is the "meat".
 *   3. THUMP      — a pitch-swept sine landing in 50–120 Hz. Real low end; without
 *                   it a gun sounds like a firecracker.
 *   4. PUNCH/BLAST— a mid bandpass burst plus a lowpassed brown-noise puff, the
 *                   muzzle blast pushing air.
 *   5. MECHANICAL — a modal (bolt/spring/casing) hit 6–16 ms after the shot.
 *
 * The environmental tail is deliberately NOT baked in: it comes from the zone
 * convolver send, so the same shot changes character as the listener moves.
 *
 * The same builder runs inside an OfflineAudioContext (to bake round-robin
 * variants) and inside the live graph (fallback before a bake lands).
 */
import { makeRng } from '../../core/Rand.js';
import {
  transientClick, sineSweep, modal, nWave, ricochetWhine, applyExpDecay, applyAD,
  bandpass, lowpass, highpass, onePoleLP, scale, granular, normalise as normArr,
} from '../synth/dsp.js';
import { noiseSlice } from '../synth/noise.js';
import { bq, gain, playData, driveStage, softClip, adEnv } from '../synth/nodes.js';
import { distantTailData } from '../synth/ir.js';

/* ------------------------------------------------------------------ *
 * Weapon class table. Every number here is a voicing decision.
 * ------------------------------------------------------------------ */
export const WEAPON_CLASSES = {
  rifle: {
    label: 'rifle', level: 0.42, mixLevel: 1.00, duration: 0.55,
    transientLevel: 1.55, transientTau: 0.00042, transientHP: 2100, crackFreq: 3400, crackGain: 8,
    bodyLen: 0.34, bodyF0: 5400, bodyF1: 265, sweep: 0.052, bodyQ: 4.4, bodyTau: 0.055,
    bodyLevel: 0.95, drive: 3.4,
    thumpF0: 195, thumpF1: 64, thumpTau: 0.075, thumpLen: 0.34, thumpLevel: 0.44,
    punchF: 820, punchQ: 1.6, punchTau: 0.070, punchLevel: 0.72,
    blastTau: 0.150, blastLP: 340, blastLevel: 0.26,
    mechDelay: 0.0105, mechLevel: 0.26, mechLen: 0.10,
    mechModes: [[2380, 1.0, 0.020], [3720, 0.55, 0.013], [5580, 0.30, 0.007], [1340, 0.50, 0.032], [880, 0.30, 0.045]],
    pitchVar: 0.035, timeVar: 0.0016, supersonic: true,
  },
  smg: {
    label: 'smg', level: 0.50, mixLevel: 0.82, duration: 0.38,
    transientLevel: 1.45, transientTau: 0.00034, transientHP: 2600, crackFreq: 4300, crackGain: 8.5,
    bodyLen: 0.24, bodyF0: 6200, bodyF1: 390, sweep: 0.036, bodyQ: 3.7, bodyTau: 0.034,
    bodyLevel: 0.86, drive: 3.0,
    thumpF0: 230, thumpF1: 90, thumpTau: 0.045, thumpLen: 0.22, thumpLevel: 0.26,
    punchF: 1250, punchQ: 1.5, punchTau: 0.045, punchLevel: 0.64,
    blastTau: 0.085, blastLP: 450, blastLevel: 0.15,
    mechDelay: 0.0075, mechLevel: 0.40, mechLen: 0.08,
    mechModes: [[2950, 1.0, 0.014], [4400, 0.6, 0.009], [6600, 0.35, 0.005], [1650, 0.45, 0.022]],
    pitchVar: 0.045, timeVar: 0.0022, supersonic: true,
  },
  shotgun: {
    label: 'shotgun', level: 0.34, mixLevel: 1.15, duration: 0.85,
    transientLevel: 2.10, transientTau: 0.00070, transientHP: 1400, crackFreq: 2300, crackGain: 5.5,
    bodyLen: 0.55, bodyF0: 4200, bodyF1: 185, sweep: 0.082, bodyQ: 3.6, bodyTau: 0.100,
    bodyLevel: 1.00, drive: 4.0,
    thumpF0: 152, thumpF1: 49, thumpTau: 0.145, thumpLen: 0.55, thumpLevel: 0.58,
    punchF: 540, punchQ: 1.3, punchTau: 0.115, punchLevel: 0.74,
    blastTau: 0.240, blastLP: 250, blastLevel: 0.42,
    mechDelay: 0.0130, mechLevel: 0.20, mechLen: 0.12,
    mechModes: [[1450, 1.0, 0.028], [2260, 0.5, 0.018], [760, 0.6, 0.050]],
    pitchVar: 0.030, timeVar: 0.0020, supersonic: false,
  },
  pistol: {
    label: 'pistol', level: 0.62, mixLevel: 0.62, duration: 0.32,
    transientLevel: 1.18, transientTau: 0.00028, transientHP: 3100, crackFreq: 5000, crackGain: 9,
    bodyLen: 0.20, bodyF0: 7000, bodyF1: 440, sweep: 0.028, bodyQ: 3.4, bodyTau: 0.028,
    bodyLevel: 0.78, drive: 2.6,
    thumpF0: 250, thumpF1: 102, thumpTau: 0.036, thumpLen: 0.18, thumpLevel: 0.19,
    punchF: 1550, punchQ: 1.4, punchTau: 0.035, punchLevel: 0.56,
    blastTau: 0.065, blastLP: 560, blastLevel: 0.11,
    mechDelay: 0.0090, mechLevel: 0.44, mechLen: 0.09,
    mechModes: [[3250, 1.0, 0.012], [5100, 0.55, 0.008], [7300, 0.3, 0.004], [1900, 0.4, 0.020]],
    pitchVar: 0.040, timeVar: 0.0018, supersonic: true,
  },
  sniper: {
    label: 'sniper', level: 0.38, mixLevel: 1.20, duration: 0.95,
    transientLevel: 1.60, transientTau: 0.00050, transientHP: 1900, crackFreq: 3100, crackGain: 8,
    bodyLen: 0.60, bodyF0: 5000, bodyF1: 235, sweep: 0.072, bodyQ: 4.8, bodyTau: 0.085,
    bodyLevel: 1.00, drive: 3.8,
    thumpF0: 175, thumpF1: 55, thumpTau: 0.120, thumpLen: 0.50, thumpLevel: 0.52,
    punchF: 700, punchQ: 1.7, punchTau: 0.100, punchLevel: 0.70,
    blastTau: 0.210, blastLP: 300, blastLevel: 0.40,
    mechDelay: 0.0150, mechLevel: 0.24, mechLen: 0.13,
    mechModes: [[1980, 1.0, 0.030], [3050, 0.5, 0.019], [720, 0.55, 0.055]],
    pitchVar: 0.025, timeVar: 0.0014, supersonic: true,
  },
};

const CLASS_ALIASES = [
  [/sniper|dmr|bolt|barrett|awp|marksman/i, 'sniper'],
  [/shot ?gun|pump|slug|buck|spas|ithaca/i, 'shotgun'],
  [/smg|mp5|mp7|ump|vector|uzi|p90|subm/i, 'smg'],
  [/pistol|hand ?gun|glock|deagle|m1911|revolver|sidearm/i, 'pistol'],
  [/rifle|ar|ak|m4|m16|scar|carbine|lmg|assault/i, 'rifle'],
];

/** Resolve whatever the weapons team hands us into a class key. */
export function classify(weapon) {
  if (!weapon) return 'rifle';
  const s = typeof weapon === 'string'
    ? weapon
    : String(weapon.class || weapon.category || weapon.type || weapon.name || 'rifle');
  if (WEAPON_CLASSES[s]) return s;
  for (const [re, key] of CLASS_ALIASES) if (re.test(s)) return key;
  return 'rifle';
}

/* ------------------------------------------------------------------ *
 * Mode variants
 * ------------------------------------------------------------------ */

function suppressedSpec(W) {
  return {
    ...W,
    label: W.label + '_suppressed',
    level: W.level * 0.46,
    duration: Math.max(0.26, W.duration * 0.55),
    transientLevel: W.transientLevel * 0.20,
    transientHP: 850,
    crackFreq: 1500, crackGain: 3,
    bodyF0: Math.min(W.bodyF0, 2400), bodyF1: Math.max(W.bodyF1 * 0.8, 150),
    bodyQ: W.bodyQ * 0.7, bodyTau: W.bodyTau * 0.55, bodyLevel: W.bodyLevel * 0.62,
    drive: W.drive * 0.6,
    thumpLevel: W.thumpLevel * 0.42, thumpTau: W.thumpTau * 0.6,
    punchLevel: W.punchLevel * 0.30, punchF: W.punchF * 0.7,
    blastLevel: W.blastLevel * 0.30, blastTau: W.blastTau * 0.5,
    mechLevel: Math.min(0.85, W.mechLevel * 2.6),
    gasHiss: 0.30,
    suppressed: true,
  };
}

function distantSpec(W, distance = 120) {
  const k = Math.min(1, Math.max(0, (distance - 25) / 200));
  return {
    ...W,
    label: W.label + '_distant',
    level: W.level * (0.55 - 0.22 * k),
    duration: 1.7,
    transientLevel: W.transientLevel * (0.55 - 0.35 * k),
    transientHP: 700,
    crackFreq: 1100, crackGain: 2,
    bodyF0: 1800 - 900 * k, bodyF1: Math.max(90, W.bodyF1 * 0.55),
    bodyTau: W.bodyTau * 1.5, bodyLevel: W.bodyLevel * 0.75, drive: 1.6,
    thumpF0: W.thumpF0 * 0.85, thumpF1: W.thumpF1 * 0.9,
    thumpTau: W.thumpTau * 1.6, thumpLevel: W.thumpLevel * 1.05,
    punchLevel: W.punchLevel * 0.35, punchF: W.punchF * 0.55,
    blastLevel: W.blastLevel * 1.1, blastTau: W.blastTau * 2.2, blastLP: 200,
    mechLevel: 0,
    distant: true, distantLP: 900 - 520 * k, distantTailRT: 1.5 + 1.1 * k, distantWet: 0.55 + 0.25 * k,
  };
}

export function specFor(weapon, mode = 'normal', distance = 0) {
  const W = WEAPON_CLASSES[classify(weapon)];
  if (mode === 'suppressed') return suppressedSpec(W);
  if (mode === 'distant') return distantSpec(W, distance || 120);
  return W;
}

/* ------------------------------------------------------------------ *
 * The builder
 * ------------------------------------------------------------------ */

/**
 * Schedule a complete dry gunshot into `dest` starting at `t0`.
 * Works on any BaseAudioContext.
 * @param {BaseAudioContext} ac
 * @param {AudioNode} dest
 * @param {number} t0
 * @param {(…)=>number} rng seeded RNG
 * @param {object} W spec from specFor()
 */
export function buildGunshot(ac, dest, t0, rng, W) {
  const sr = ac.sampleRate;
  const nodes = [];

  // Per-shot micro-variation: pitch, timing and layer balance. This is what stops
  // sustained automatic fire from sounding like a loop.
  const pitch = 1 + (rng() * 2 - 1) * (W.pitchVar || 0.03);
  const jitter = (rng() * 2 - 1) * (W.timeVar || 0.0015);
  const t = Math.max(0, t0 + jitter);
  const bal = () => 0.85 + rng() * 0.3;

  const master = gain(ac, W.level);
  master.connect(dest);
  nodes.push(master);

  let sink = master;
  if (W.distant) {
    // heavy low-pass + a long diffuse tail = gunfire from across the map
    const lp1 = bq(ac, 'lowpass', W.distantLP, 0.8);
    const lp2 = bq(ac, 'lowpass', W.distantLP * 1.4, 0.7);
    const hs = bq(ac, 'highshelf', 2000, 0.7, -20);
    lp1.connect(lp2).connect(hs);
    const conv = ac.createConvolver();
    conv.normalize = true;
    const tail = distantTailData(sr, 0x9911, W.distantTailRT);
    const tb = ac.createBuffer(2, tail.l.length, sr);
    tb.copyToChannel(tail.l, 0);
    tb.copyToChannel(tail.r, 1);
    conv.buffer = tb;
    const dry = gain(ac, 1 - W.distantWet * 0.55);
    const wet = gain(ac, W.distantWet);
    hs.connect(dry).connect(master);
    hs.connect(conv).connect(wet).connect(master);
    sink = lp1;
    nodes.push(lp1, lp2, hs, conv, dry, wet);
  }

  /* ---- 1. transient ---------------------------------------------------- */
  if (W.transientLevel > 0.001) {
    const data = transientClick(sr, rng, {
      seconds: 0.008, tau: W.transientTau * (0.9 + rng() * 0.2), hp: W.transientHP,
    });
    const g = gain(ac, W.transientLevel * bal());
    const eq = bq(ac, 'peaking', W.crackFreq * pitch, 1.1, W.crackGain);
    const hp = bq(ac, 'highpass', W.transientHP * 0.55, 0.7);
    g.connect(eq).connect(hp).connect(sink);
    nodes.push(playData(ac, data, g, t, { rate: pitch }), g, eq, hp);
  }

  /* ---- 2. body: resonant down-sweep ------------------------------------ */
  {
    const n = Math.floor(sr * W.bodyLen);
    const data = noiseSlice(rng() < 0.5 ? 'white' : 'pink', n, rng);
    const g = gain(ac, 0);
    const f = bq(ac, 'lowpass', W.bodyF0, W.bodyQ);
    const hp = bq(ac, 'highpass', 45, 0.6);
    const dr = driveStage(ac, W.drive);
    f.connect(dr).connect(hp).connect(g).connect(sink);
    f.frequency.setValueAtTime(W.bodyF0 * pitch, t);
    f.frequency.exponentialRampToValueAtTime(Math.max(40, W.bodyF1 * pitch), t + W.sweep);
    f.frequency.exponentialRampToValueAtTime(Math.max(35, W.bodyF1 * 0.62 * pitch), t + W.sweep * 4);
    f.Q.setValueAtTime(W.bodyQ, t);
    f.Q.linearRampToValueAtTime(W.bodyQ * 0.35, t + W.sweep * 3);
    adEnv(g.gain, t, W.bodyLevel * bal(), 0.0007, W.bodyTau);
    nodes.push(playData(ac, data, f, t, { rate: pitch }), g, f, hp, dr);
  }

  /* ---- 3. thump: real low end ------------------------------------------ */
  if (W.thumpLevel > 0.001) {
    const data = sineSweep(sr, W.thumpLen, W.thumpF0, W.thumpF1, W.thumpTau, rng, 2.6);
    const g = gain(ac, W.thumpLevel * bal());
    const lp = bq(ac, 'lowpass', 240, 0.8);
    const sat = driveStage(ac, 1.8);
    lp.connect(sat).connect(g).connect(sink);
    nodes.push(playData(ac, data, lp, t + 0.0008, { rate: pitch }), g, lp, sat);
  }

  /* ---- 4a. mid punch ---------------------------------------------------- */
  if (W.punchLevel > 0.001) {
    const n = Math.floor(sr * Math.min(0.4, W.punchTau * 6));
    const data = noiseSlice('white', n, rng);
    bandpass(data, sr, W.punchF * pitch, W.punchQ);
    applyAD(data, sr, 0.0012, W.punchTau);
    normArr(data, 1);
    const g = gain(ac, W.punchLevel * bal());
    g.connect(sink);
    nodes.push(playData(ac, data, g, t, { rate: pitch }), g);
  }

  /* ---- 4b. muzzle blast puff ------------------------------------------- */
  if (W.blastLevel > 0.001) {
    const n = Math.floor(sr * Math.min(0.9, W.blastTau * 5));
    const data = noiseSlice('brown', n, rng);
    lowpass(data, sr, W.blastLP, 0.9);
    applyAD(data, sr, 0.0025, W.blastTau);
    normArr(data, 1);
    const g = gain(ac, W.blastLevel * bal());
    g.connect(sink);
    nodes.push(playData(ac, data, g, t + 0.0012, { rate: 1 }), g);
  }

  /* ---- 4c. suppressor gas hiss ----------------------------------------- */
  if (W.gasHiss) {
    const n = Math.floor(sr * 0.14);
    const data = noiseSlice('white', n, rng);
    bandpass(data, sr, 2600, 0.9);
    applyAD(data, sr, 0.004, 0.030);
    normArr(data, 1);
    const g = gain(ac, W.gasHiss * bal());
    g.connect(sink);
    nodes.push(playData(ac, data, g, t + 0.004, {}), g);
  }

  /* ---- 5. mechanical: bolt / spring / casing --------------------------- */
  if (W.mechLevel > 0.001) {
    const data = modal(sr, W.mechLen, W.mechModes, rng, 0.05);
    applyExpDecay(data, sr, W.mechLen * 0.28);
    normArr(data, 1);
    const g = gain(ac, W.mechLevel * bal());
    const hp = bq(ac, 'highpass', 500, 0.7);
    hp.connect(g).connect(sink);
    const md = W.mechDelay * (0.85 + rng() * 0.3);
    nodes.push(playData(ac, data, hp, t + md, { rate: 0.94 + rng() * 0.12 }), g, hp);

    // spring ring-out — a light Karplus resonance under the bolt
    if (!W.distant) {
      const spring = noiseSlice('white', Math.floor(sr * 0.05), rng);
      bandpass(spring, sr, 4200 + rng() * 1800, 8);
      applyExpDecay(spring, sr, 0.011);
      normArr(spring, 1);
      const sg = gain(ac, W.mechLevel * 0.35);
      sg.connect(sink);
      nodes.push(playData(ac, spring, sg, t + md + 0.004, {}), sg);
    }
  }

  return { nodes, master, duration: W.duration };
}

/** Bake-friendly wrapper: renders the dry shot through a soft clipper. */
export function bakeGunshot(ac, dest, seed, W) {
  const clip = softClip(ac, 1.25);
  clip.connect(dest);
  buildGunshot(ac, clip, 0, makeRng(seed >>> 0), W);
}

/* ------------------------------------------------------------------ *
 * Supersonic crack & near-miss
 * ------------------------------------------------------------------ */

/** Ballistic N-wave for a round passing close to the listener. */
export function buildCrack(ac, dest, t0, rng, { level = 0.55, us = 210 } = {}) {
  const sr = ac.sampleRate;
  const data = nWave(sr, rng, us * (0.8 + rng() * 0.4), 0.03);
  const g = gain(ac, level * (0.85 + rng() * 0.3));
  const hp = bq(ac, 'highpass', 420, 0.7);
  const pk = bq(ac, 'peaking', 2400 + rng() * 1200, 1.4, 6);
  hp.connect(pk).connect(g).connect(dest);
  playData(ac, data, hp, t0, { rate: 0.9 + rng() * 0.2 });
  // the small "zip" of the wake
  const wake = noiseSlice('white', Math.floor(sr * 0.06), rng);
  bandpass(wake, sr, 1800 + rng() * 900, 2.2);
  applyAD(wake, sr, 0.002, 0.012);
  normArr(wake, 1);
  const wg = gain(ac, level * 0.35);
  wg.connect(dest);
  playData(ac, wake, wg, t0 + 0.0015, {});
  return { duration: 0.09 };
}

/** Subsonic near-miss whoosh (a round that did not break the sound barrier). */
export function buildWhizby(ac, dest, t0, rng, { level = 0.5 } = {}) {
  const sr = ac.sampleRate;
  const n = Math.floor(sr * 0.22);
  const data = noiseSlice('white', n, rng);
  const f0 = 900 + rng() * 500;
  for (let i = 0; i < n; i++) {
    const k = i / n;
    data[i] *= Math.sin(Math.PI * Math.min(1, k * 1.6)) * Math.exp(-k * 2.2);
  }
  bandpass(data, sr, f0, 1.6);
  normArr(data, 1);
  const g = gain(ac, level);
  const f = bq(ac, 'bandpass', f0 * 1.5, 1.4);
  f.frequency.setValueAtTime(f0 * 1.55, t0);
  f.frequency.exponentialRampToValueAtTime(f0 * 0.55, t0 + 0.16);
  f.connect(g).connect(dest);
  playData(ac, data, f, t0, {});
  return { duration: 0.24 };
}

/* ------------------------------------------------------------------ *
 * Ricochet / debris (used by Impacts too)
 * ------------------------------------------------------------------ */

export function buildRicochet(ac, dest, t0, rng, { level = 0.55, base = 1500 } = {}) {
  const sr = ac.sampleRate;
  const seconds = 0.34 + rng() * 0.3;
  const data = ricochetWhine(sr, seconds, base * (0.7 + rng() * 0.8), rng);
  const g = gain(ac, level);
  const hp = bq(ac, 'highpass', 600, 0.7);
  hp.connect(g).connect(dest);
  playData(ac, data, hp, t0, {});
  return { duration: seconds + 0.05 };
}

export function buildDebris(ac, dest, t0, rng, { level = 0.4, kind = 'concrete', seconds = 0.55 } = {}) {
  const sr = ac.sampleRate;
  const src = noiseSlice('white', Math.floor(sr * 0.05), rng);
  if (kind === 'glass') bandpass(src, sr, 5200, 3);
  else if (kind === 'metal') bandpass(src, sr, 3000, 4);
  else bandpass(src, sr, 1500, 1.6);
  applyExpDecay(src, sr, 0.006);
  const data = granular(sr, seconds, src, rng, {
    grains: kind === 'glass' ? 34 : 20, grainMs: 12, spread: 0.55, pitchJitter: 0.55, gainJitter: 0.8,
  });
  for (let i = 0; i < data.length; i++) data[i] *= Math.exp(-(i / sr) / (seconds * 0.4));
  normArr(data, 1);
  const g = gain(ac, level);
  const hp = bq(ac, 'highpass', kind === 'glass' ? 1800 : 400, 0.7);
  hp.connect(g).connect(dest);
  playData(ac, data, hp, t0, {});
  return { duration: seconds + 0.05 };
}

export { onePoleLP, highpass, scale };
